// ModalTag.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Typography,
  FormControlLabel, Switch
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";

const API = "https://api.hmsaa.com.mx:5001/api";

export default function ModalTag({
  open, onClose,
  departamentos,
  activos,
  lastActivoId,
  refresh
}) {
  const [form, setForm] = useState({
    id_departamento: "",
    id_activo: "",
    desde: "",
    hasta: "",
    unico: ""
  });

  const [bulk, setBulk] = useState(false);

  /* ================= INIT ================= */
  useEffect(() => {
    if (!open) return;

    setForm({
      id_departamento: "",
      id_activo: "",
      desde: "",
      hasta: "",
      unico: ""
    });

    setBulk(false);
  }, [open]);

  /* ================= FILTROS ================= */
  const activosFiltrados = useMemo(() => {
    return activos.filter(
      a => String(a.id_departamento) === String(form.id_departamento)
    );
  }, [activos, form.id_departamento]);

  const activo = activosFiltrados.find(
    a => String(a.id_activo) === String(form.id_activo)
  );

  const preview = (n) =>
    activo && n
      ? `${activo.departamento_abreviatura}-${activo.numero}-${String(n).padStart(3, "0")}`
      : "—";

  /* ================= VALIDACIÓN ================= */
  const validar = () => {
    if (!form.id_departamento) {
      Swal.fire("Faltan datos", "Selecciona un departamento", "warning");
      return false;
    }
    if (!form.id_activo) {
      Swal.fire("Faltan datos", "Selecciona un activo", "warning");
      return false;
    }

    if (bulk) {
      if (!form.desde || !form.hasta) {
        Swal.fire("Faltan datos", "Desde y Hasta son obligatorios", "warning");
        return false;
      }
      if (+form.desde > +form.hasta) {
        Swal.fire("Rango inválido", "Desde no puede ser mayor que Hasta", "warning");
        return false;
      }
    } else {
      if (!form.unico) {
        Swal.fire("Faltan datos", "El correlativo es obligatorio", "warning");
        return false;
      }
    }
    return true;
  };

  /* ================= SAVE ================= */
  const guardar = async () => {
    if (!validar()) return;

    try {
      if (bulk) {
        const res = await axios.post(`${API}/tags/bulk`, {
          id_activo: Number(form.id_activo),
          desde: Number(form.desde),
          hasta: Number(form.hasta)
        });

        Swal.fire({
          icon: "success",
          title: "Tags creados",
          text: `Creados: ${res.data.created} | Omitidos: ${res.data.skipped}`
        });
      } else {
        await axios.post(`${API}/tags`, {
          id_activo: Number(form.id_activo),
          correlativo: Number(form.unico)
        });

        Swal.fire({
          icon: "success",
          title: "Tag creado",
          text: preview(form.unico)
        });
      }

      await refresh();
      onClose();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.error || "Error inesperado";

      Swal.fire(
        status === 409 ? "Duplicado" : "Error",
        msg,
        status === 409 ? "warning" : "error"
      );
    }
  };

  /* ================= UI ================= */
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nuevo Tag</DialogTitle>

      <DialogContent>
        {/* DEPARTAMENTO */}
        <TextField
          fullWidth
          select
          label="Departamento"
          sx={{ mb: 2 }}
          value={form.id_departamento}
          onChange={e =>
            setForm({
              ...form,
              id_departamento: e.target.value,
              id_activo: "",
              desde: "",
              hasta: "",
              unico: ""
            })
          }
        >
          {departamentos.map(d => (
            <MenuItem key={d.id_departamento} value={d.id_departamento}>
              {d.nombre}
            </MenuItem>
          ))}
        </TextField>

        {/* ACTIVO */}
        <TextField
          fullWidth
          select
          label="Activo"
          sx={{ mb: 2 }}
          value={form.id_activo}
          disabled={!form.id_departamento}
          onChange={e => setForm({ ...form, id_activo: e.target.value })}
        >
          {activosFiltrados.map(a => (
            <MenuItem key={a.id_activo} value={a.id_activo}>
              {a.numero} — {a.descripcion}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch
              checked={bulk}
              onChange={() => setBulk(!bulk)}
            />
          }
          label="Crear grupo de tags"
        />

        {bulk ? (
          <>
            <TextField
              fullWidth
              label="Desde"
              type="number"
              sx={{ mt: 2 }}
              value={form.desde}
              onChange={e => setForm({ ...form, desde: e.target.value })}
            />
            <TextField
              fullWidth
              label="Hasta"
              type="number"
              sx={{ mt: 2 }}
              value={form.hasta}
              onChange={e => setForm({ ...form, hasta: e.target.value })}
            />
            <Typography sx={{ mt: 2 }}>
              Ejemplo: {preview(form.desde)} → {preview(form.hasta)}
            </Typography>
          </>
        ) : (
          <>
            <TextField
              fullWidth
              label="Correlativo"
              type="number"
              sx={{ mt: 2 }}
              value={form.unico}
              onChange={e => setForm({ ...form, unico: e.target.value })}
            />
            <Typography sx={{ mt: 2 }}>
              <strong>Código:</strong> {preview(form.unico)}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={guardar}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
